
import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { FiUploadCloud, FiArrowRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import Button from '../Button';
import Select from '../Select';
import Api from '../../Api';
import './style.css';

const DB_FIELDS = [
  { value: 'name', label: 'Nome / Razão Social' },
  { value: 'trade_name', label: 'Nome Fantasia' },
  { value: 'document', label: 'CPF / CNPJ' },
  { value: 'company_ref', label: 'Empresa (ID ou Nome)' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'mobile', label: 'Celular' },
  { value: 'zip_code', label: 'CEP' },
  { value: 'street', label: 'Logradouro' },
  { value: 'number', label: 'Número' },
  { value: 'complement', label: 'Complemento' },
  { value: 'neighborhood', label: 'Bairro' },
  { value: 'city', label: 'Cidade' },
  { value: 'state', label: 'Estado (UF)' },
  { value: 'notes', label: 'Observações' },
  { value: 'type', label: 'Tipo (PF/PJ)' },
];

export default function CustomerImportModal({ isOpen, onClose, onSuccess, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, map
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { headerName: dbField }
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || '');

  React.useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const findCompany = (companiesList, ref) => {
    if (!ref) return null;
    const normalizedRef = normalizeString(ref);
    const exactId = companiesList.find(c => String(c.id) === String(ref));
    if (exactId) return exactId;
    return companiesList.find(c => {
      const name1 = normalizeString(c.corporate_name);
      const name2 = normalizeString(c.trade_name);
      return (name1 && (name1.includes(normalizedRef) || normalizedRef.includes(name1))) ||
             (name2 && (name2.includes(normalizedRef) || normalizedRef.includes(name2)));
    }) || null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Array of arrays

        if (data.length < 2) {
          toast.warning('O arquivo parece estar vazio ou sem dados.');
          return;
        }

        const fileHeaders = data[0];
        const rows = data.slice(1);

        setHeaders(fileHeaders);
        setFileData(rows);
        
        // Auto-map based on similar names
        const initialMapping = {};
        
        const normalize = (str) => String(str).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        fileHeaders.forEach(header => {
          const normHeader = normalize(header);
          
          if (normHeader.includes('nome') || normHeader.includes('razao')) initialMapping[header] = 'name';
          else if (normHeader.includes('fantasia')) initialMapping[header] = 'trade_name';
          else if (normHeader.includes('cpf') || normHeader.includes('cnpj') || normHeader.includes('documento')) initialMapping[header] = 'document';
          else if (normHeader.includes('empresa') || normHeader.includes('company')) initialMapping[header] = 'company_ref';
          else if (normHeader.includes('email') || normHeader.includes('e-mail')) initialMapping[header] = 'email';
          else if (normHeader.includes('telefone')) initialMapping[header] = 'phone';
          else if (normHeader.includes('celular') || normHeader.includes('mobile')) initialMapping[header] = 'mobile';
          else if (normHeader.includes('cep')) initialMapping[header] = 'zip_code';
          else if (normHeader.includes('rua') || normHeader.includes('logradouro') || normHeader.includes('endereco')) initialMapping[header] = 'street';
          else if (normHeader.includes('numero')) initialMapping[header] = 'number';
          else if (normHeader.includes('complemento')) initialMapping[header] = 'complement';
          else if (normHeader.includes('bairro')) initialMapping[header] = 'neighborhood';
          else if (normHeader.includes('cidade')) initialMapping[header] = 'city';
          else if (normHeader.includes('estado') || normHeader.includes('uf')) initialMapping[header] = 'state';
          else if (normHeader.includes('obs') || normHeader.includes('nota')) initialMapping[header] = 'notes';
          else if (normHeader.includes('tipo')) initialMapping[header] = 'type';
        });
        
        setMapping(initialMapping);
        setStep('map');
      } catch (error) {
        console.error(error);
        toast.error('Erro ao ler o arquivo.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (header, dbField) => {
    setMapping(prev => ({ ...prev, [header]: dbField }));
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      // Transform data based on mapping
      const itemsRaw = fileData.map(row => {
        const item = {};
        let rowCompanyId = selectedCompanyId;
        
        headers.forEach((header, index) => {
          const dbField = mapping[header];
          if (dbField) {
            let value = row[index];
            if (value !== undefined && value !== null) {
                // Ensure strings
                value = String(value).trim();
            }
            if (dbField === 'company_ref') {
              const matchedCompany = findCompany(companies, value);
              if (matchedCompany) rowCompanyId = matchedCompany.id;
              else if (!isNaN(Number(value))) rowCompanyId = Number(value);
            } else {
              item[dbField] = value;
            }
          }
        });

        // Set default type if not mapped
        if (!item.type) {
            item.type = 'PJ'; // Default
        }

        item.company_id = rowCompanyId;
        return item;
      }).filter(i => i.name); // Ensure name exists

      if (itemsRaw.length === 0) {
        toast.warning('Nenhum dado válido para importar. Certifique-se de mapear a coluna "Nome / Razão Social".');
        setLoading(false);
        return;
      }

      const byCompany = itemsRaw.reduce((acc, item) => {
        if (!acc[item.company_id]) acc[item.company_id] = [];
        acc[item.company_id].push(item);
        return acc;
      }, {});

      let total = 0;
      for (const cId of Object.keys(byCompany)) {
        const res = await Api.importCustomers(cId, { customers: byCompany[cId] });
        if (Array.isArray(res)) total += res.length;
        else total += byCompany[cId].length;
      }
      
      toast.success(`${total} clientes processados com sucesso!`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar dados. Verifique se o formato está correto.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileData([]);
    setHeaders([]);
    setMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="Importar Clientes"
      size="lg"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
            {step === 'upload' && (
                <div className="upload-step-container">
                    {companies.length > 0 && (
                       <div className="company-select-container" style={{ marginBottom: '1rem' }}>
                           <Select
                               label="Selecione a empresa padrão para importação"
                               options={[{ value: '', label: 'SELECIONE...' }, ...companies.map(c => ({ value: c.id, label: c.corporate_name || c.trade_name }))]}
                               value={selectedCompanyId}
                               onChange={e => setSelectedCompanyId(e.target.value)}
                               searchable
                           />
                       </div>
                    )}
                <div 
                    className="upload-area"
                    onClick={() => {
                        fileInputRef.current.click();
                    }}
                >
                    <FiUploadCloud className="upload-icon" />
                    <p className="upload-text">
                        Clique para selecionar um arquivo Excel (.xlsx) ou CSV
                    </p>
                    <>
                        <p className="upload-subtext">O arquivo deve conter cabeçalhos na primeira linha.</p>
                        <p className="upload-subtext">Exemplo: Empresa | Nome | CNPJ | Email | Telefone</p>
                    </>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
                </div>
            )}

            {step === 'map' && (
                <div className="map-step">
                    <p className="step-description">Mapeie as colunas do seu arquivo para os campos do sistema.</p>
                    
                    <div className="mapping-grid">
                        {headers.map((header, idx) => (
                            <div key={idx} className="mapping-row">
                                <div className="file-header">
                                    <span className="label">Coluna do Arquivo:</span>
                                    <strong>{header}</strong>
                                    {fileData[0] && (
                                        <span className="example-value">Ex: {fileData[0][idx]}</span>
                                    )}
                                </div>
                                <div className="arrow-icon">
                                    <FiArrowRight />
                                </div>
                                <div className="db-field-select">
                                    <Select 
                                        name={`map-${idx}`}
                                        value={mapping[header] || ''}
                                        onChange={(e) => handleMappingChange(header, e.target.value)}
                                        options={[
                                            { value: '', label: 'Ignorar' },
                                            ...DB_FIELDS
                                        ]}
                                        placeholder="Selecione o campo..."
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="modal-actions">
          <Button onClick={() => step === 'upload' ? onClose() : reset()} className="cancel-button" disabled={loading}>
            {step === 'upload' ? 'Cancelar' : 'Voltar'}
          </Button>
          
          {step === 'map' && (
            <Button onClick={handleImport} disabled={loading}>
              {loading ? 'Importando...' : 'Importar Dados'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
